import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  type ColumnDef,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useReportSummaries } from "@/hooks/useReportQueries";
import { handleApiError, isNotFoundError } from "@/hooks/useApiQueries";
import {
  Button,
  ErrorDisplay,
  HeadlessTable,
  LabelValue,
  Loader,
  NoData,
  ReportSummaryStatusBadge,
  StatusIndicator,
  SummaryInfo,
  Tooltip,
  TooltipIndicator,
} from "@/shared/components";
import { useBreadcrumb } from "@/shared/hooks/useBreadcrumb";
import type { ReportSummary } from "@/generated/ui-api";
import { useReportSummaryStatuses } from "@/hooks/useClassifierQueries";
import { ArrowIcon } from "@/assets/icons";
import { useAuth } from "@/auth";

const getLastMonthEndDate = (): Date => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 0);
};

const getLastMonthEndDateString = (): string => {
  return getLastMonthEndDate().toISOString().split("T")[0];
};

const getTwoYearsAgoDateString = (): string => {
  const pastDate = getLastMonthEndDate();
  pastDate.setFullYear(pastDate.getFullYear() - 2);
  return pastDate.toISOString().split("T")[0];
};

export default function ReportSummaryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { representedEntity } = useAuth();

  const startDate = getTwoYearsAgoDateString();
  const endDate = getLastMonthEndDateString();

  const {
    data: summaryData,
    isLoading: loading,
    error,
    isError,
  } = useReportSummaries(startDate, endDate, !!representedEntity);

  const { data: reportSummaryStatuses } = useReportSummaryStatuses();

  const getReportSummaryStatusLabel = useCallback(
    (key?: string) => {
      return (
        reportSummaryStatuses?.find(s => s.key === key)?.label || key || ""
      );
    },
    [reportSummaryStatuses]
  );

  const { setBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    if (summaryData) {
      const breadcrumbs = [
        { label: t("breadcrumbs.reports"), path: "/reports/summaries" },
      ];
      setBreadcrumbs(breadcrumbs);
    }
  }, [summaryData, t, setBreadcrumbs]);

  type ReportSummaryWithOptionalId = ReportSummary & { id?: number };

  interface TableMeta {
    tradeDirection: string;
  }

  const columns = useMemo<ColumnDef<ReportSummaryWithOptionalId>[]>(
    () => [
      {
        accessorKey: "reportingPeriod",
        id: "reportingPeriod",
        header: t("summaryPage.table.reportingPeriod"),
        size: 100,
        cell: info => {
          const period = info.getValue() as ReportSummary["reportingPeriod"];
          if (!period) {
            return "";
          }
          return (
            <div className="flex justify-center items-center h-full">
              {`${period.reportingYear}-${String(
                period.reportingMonth
              ).padStart(2, "0")}`}
            </div>
          );
        },
      },
      {
        accessorKey: "reportSummaryStatus",
        id: "status",
        header: t("summaryPage.table.status"),
        size: 100,
        cell: info => {
          const status = info.getValue() as "FULFILLED" | "PENDING";
          const hasObligation = info.row.original.reportingObligation;

          const showBadge =
            status === "FULFILLED" || (status === "PENDING" && hasObligation);

          return showBadge ? (
            <div className="flex justify-center items-center h-full">
              <ReportSummaryStatusBadge status={status}>
                {getReportSummaryStatusLabel(status)}
              </ReportSummaryStatusBadge>
            </div>
          ) : (
            ""
          );
        },
      },
      {
        accessorKey: "reportingObligation",
        id: "obligation",
        header: t("summaryPage.table.obligation"),
        size: 80,
        cell: info => {
          const hasObligation = info.getValue() as boolean;
          return (
            <div className="flex justify-center items-center h-full">
              {hasObligation ? (
                <Tooltip content={t("summaryPage.tooltip.reportingObligation")}>
                  <div className="flex items-center">
                    <StatusIndicator status="neutral" size="lg" />
                  </div>
                </Tooltip>
              ) : (
                <Tooltip
                  content={t("summaryPage.tooltip.noReportingObligation")}
                >
                  <div className="flex items-center">
                    <div className="w-4 h-4 border-2 border-gray-300 rounded-full" />
                  </div>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "reportIsLate",
        id: "isLate",
        header: t("summaryPage.table.isLate"),
        size: 80,
        cell: info => (
          <div className="flex justify-center items-center h-full">
            {(info.getValue() as boolean) && (
              <Tooltip
                content={t("summaryPage.tooltip.reportIsLate")}
                variant="error"
              >
                <div className="flex items-center">
                  <StatusIndicator status="error" size="lg" />
                </div>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        accessorKey: "reportWorkUnfinished",
        id: "workUnfinished",
        header: t("summaryPage.table.workUnfinished"),
        size: 40,
        cell: info => (
          <div className="flex justify-center items-center h-full">
            {(info.getValue() as boolean) && (
              <TooltipIndicator
                content={t("summaryPage.tooltip.workUnfinished")}
                variant="info"
                placement="top"
                size={20}
              />
            )}
          </div>
        ),
      },
      {
        id: "actions",
        header: "",
        size: 150,
        cell: info => {
          const status = info.row.original.reportSummaryStatus;
          const hasReport = !!info.row.original.id;
          const labelKey =
            status === "FULFILLED" && hasReport
              ? "summaryPage.buttons.open"
              : "summaryPage.buttons.submit";

          const tradeDirection = (info.table.options.meta as TableMeta)
            ?.tradeDirection;

          return (
            <div className="flex gap-2 justify-end items-center h-full">
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  const id = info.row.original.id;
                  const path = id ? `/report/${id}` : "/report";
                  navigate(path, {
                    state: {
                      summary: info.row.original,
                      tradeDirection,
                    },
                  });
                }}
              >
                {t(labelKey)}
              </Button>
            </div>
          );
        },
      },
    ],
    [t, navigate, getReportSummaryStatusLabel]
  );

  const importTable = useReactTable({
    data: summaryData?.reportSummariesImport ?? [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      tradeDirection: "IMPORT",
    },
  });

  const exportTable = useReactTable({
    data: summaryData?.reportSummariesExport ?? [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      tradeDirection: "EXPORT",
    },
  });

  const getRowClassName = (
    rows: ReportSummaryWithOptionalId[],
    row: ReportSummaryWithOptionalId,
    index: number
  ) => {
    let className = "h-16";
    if (index === 0) return className;
    const prevRow = rows[index - 1];
    if (
      prevRow.reportingPeriod &&
      row.reportingPeriod &&
      prevRow.reportingPeriod.reportingYear !==
        row.reportingPeriod.reportingYear
    ) {
      className += " border-t-2 border-t-gray-300";
    }
    return className;
  };

  if (!representedEntity) {
    return <Loader />;
  }

  if (loading) {
    return <Loader />;
  }

  if (isError) {
    if (isNotFoundError(error)) {
      return <NoData message={t("summaryPage.businessEntityNotFound")} />;
    }
    return <ErrorDisplay message={handleApiError(error, t)} />;
  }

  if (!summaryData) {
    return <NoData />;
  }

  const importRows = summaryData?.reportSummariesImport ?? [];
  const exportRows = summaryData?.reportSummariesExport ?? [];

  return (
    <div>
      <SummaryInfo title={t("summaryPage.title")}>
        <LabelValue
          label={t("summaryPage.reportInfo.name")}
          value={summaryData.businessEntityName}
        />
        <LabelValue
          label={t("summaryPage.reportInfo.vatCode")}
          value={summaryData.businessEntityIdentifierVAT}
        />
      </SummaryInfo>

      <hr className="my-6 border-gray-200" />

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <ArrowIcon direction="right" size={28} />
            <h2 className="text-3xl font-bold text-black">
              {t("summaryPage.import")}
            </h2>
          </div>
          <HeadlessTable
            table={importTable}
            rowClassName={(row, index) =>
              getRowClassName(importRows, row, index)
            }
          />
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <ArrowIcon direction="left" size={28} />
            <h2 className="text-3xl font-bold text-black">
              {t("summaryPage.export")}
            </h2>
          </div>
          <HeadlessTable
            table={exportTable}
            rowClassName={(row, index) =>
              getRowClassName(exportRows, row, index)
            }
          />
        </div>
      </div>

      <p className="mt-8 text-sm text-gray-500">
        {t("summaryPage.timePeriod")}
      </p>
    </div>
  );
}
